import './conversation-health-professional-modal.scss';

import { useMutation } from '@apollo/client';
import { debounce, kebabCase, map } from 'lodash';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useHealthProfessionalItems } from '../../../../data/data/health-professionals/use-health-professional-items';
import { useHealthProfessionalItemsFromOtherHealthProvider } from '../../../../data/data/health-professionals/use-health-professional-items-from-other-health-providers';
import {
  ConversationItemDataFragment,
  HealthProfessionalItemDataFragment,
  HealthProfessionalStatus,
  SortEnumType,
  UpdateConversationItemHealthProfessionalDocument,
} from '../../../../data/types/api/graphql';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { ContextMenuState } from '../../../../data/utils/use-context-menu';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { ContextModal } from '../../../../elements/context-menu/context-modal';
import { FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputField } from '../../../../elements/form/input/input-field';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';

enum ConversationHealthProfessionalType {
  CURRENT = 'CURRENT',
  OTHER = 'OTHER',
  SELF = 'SELF',
}

type ConversationHealthProfessionalContextModalProps = {
  state: ContextMenuState | false;
  healthProviderId: string | null | undefined;
  conversationItem: ConversationItemDataFragment;
  onSaved?: (conversationItem: ConversationItemDataFragment) => void;
  onClose?: () => void;
};

export function ConversationHealthProfessionalContextModal({
  state,
  healthProviderId,
  conversationItem,
  onSaved,
  onClose,
}: ConversationHealthProfessionalContextModalProps) {
  const intl = useIntl();

  const { showAlertMessage, user } = useAppContext();

  const [searchHealthProfessional, setSearchHealthProfessional] =
    useState<string>('');

  const onHandleSearchHealthProfessional = useCallback(
    (search: string) => {
      setSearchHealthProfessional?.(search);
    },
    [setSearchHealthProfessional]
  );

  const onHandleSearchHealthProfessionalDebounce = useMemo(
    () => debounce(onHandleSearchHealthProfessional, 250),
    [onHandleSearchHealthProfessional]
  );

  const {
    data: healthProfessionalItemsData,
    loading: healthProfessionalItemsLoading,
  } = useHealthProfessionalItems({
    healthProviderId,
    search: searchHealthProfessional,
    statuses: [HealthProfessionalStatus.Active],
    sort: {
      firstName: SortEnumType.Asc,
    },
  });

  const {
    data: otherHealthProfessionalItemsData,
    loading: otherHealthProfessionalItemsLoading,
  } = useHealthProfessionalItemsFromOtherHealthProvider({
    healthProviderId,
    search: searchHealthProfessional,
    statuses: [HealthProfessionalStatus.Active],
    sort: {
      firstName: SortEnumType.Asc,
    },
  });

  const loading = useMemo(
    () => healthProfessionalItemsLoading || otherHealthProfessionalItemsLoading,
    [healthProfessionalItemsLoading, otherHealthProfessionalItemsLoading]
  );

  const healthProfessionalItems = useMemo(() => {
    let self: HealthProfessionalItemDataFragment[] = [];
    // Check if the user is in healthProfessionalItemsData
    const foundInCurrent = healthProfessionalItemsData?.items.find(
      x => x.id === user?.id
    );
    if (foundInCurrent) {
      self = [foundInCurrent];
    } else {
      // Check if the user is in otherHealthProfessionalItemsData
      const foundInOther = otherHealthProfessionalItemsData?.items.find(
        x => x.id === user?.id
      );
      if (foundInOther) {
        self = [foundInOther];
      }
    }

    return {
      [ConversationHealthProfessionalType.SELF]: self,
      [ConversationHealthProfessionalType.CURRENT]:
        healthProfessionalItemsData?.items ?? [],
      [ConversationHealthProfessionalType.OTHER]:
        otherHealthProfessionalItemsData?.items ?? [],
    };
  }, [
    healthProfessionalItemsData?.items,
    otherHealthProfessionalItemsData?.items,
    user?.id,
  ]);

  const [
    updateConversationItemHealthProfessional,
    { loading: updateConversationItemHealthProfessionalLoading },
  ] = useMutation(UpdateConversationItemHealthProfessionalDocument);

  const onHandleHealthProfessional = useCallback(
    async (
      value: {
        healthProviderId: string;
        healthProfessionalId: string;
      } | null
    ) => {
      try {
        const result = await updateConversationItemHealthProfessional({
          variables: {
            id: conversationItem?.id ?? '',
            healthProviderId: value?.healthProviderId ?? null,
            healthProfessionalId: value?.healthProfessionalId ?? null,
          },
        });

        if (result.data?.conversationAssign.conversation) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'conversation.conversation-health-provider.success',
            }),
          });

          onSaved?.(result.data?.conversationAssign.conversation);

          onClose?.();
        } else {
          throw new Error(
            'An unknown error has occurred in the server respond.'
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    },
    [
      intl,
      conversationItem,
      updateConversationItemHealthProfessional,
      showAlertMessage,
      onSaved,
      onClose,
    ]
  );

  const { getFullName } = useFormatters();

  return (
    <ContextModal state={state}>
      <FormRow>
        <InputField
          type="search"
          name="search"
          placeholder={intl.formatMessage({
            id: 'common.health-professionals-search-placeholders',
          })}
          icon={BaseInputIcon.SEARCH}
          onChange={onHandleSearchHealthProfessionalDebounce}
        />
      </FormRow>

      {map(healthProfessionalItems, (options, key) => (
        <Fragment key={key}>
          <h4>
            <CustomFormattedMessage id={`common.${kebabCase(key)}-practice`} />
          </h4>

          {loading && (
            <p>
              <CustomFormattedMessage id="common.loading" />
            </p>
          )}

          {!loading && options.length === 0 ? (
            <p>
              <CustomFormattedMessage id="common.table.no-data" />
            </p>
          ) : (
            <div className="conversation-health-professionals-list">
              {options.map(option => (
                <div
                  key={option.id}
                  className="conversation-health-professionals-list__item"
                >
                  <button
                    type="button"
                    className={
                      option.id === conversationItem.healthProfessional?.id
                        ? 'active'
                        : ''
                    }
                    disabled={updateConversationItemHealthProfessionalLoading}
                    onClick={() =>
                      onHandleHealthProfessional(
                        option.id !== conversationItem.healthProfessional?.id
                          ? {
                              healthProviderId: option.healthProvider.id,
                              healthProfessionalId: option.id,
                            }
                          : null
                      )
                    }
                  >
                    {getFullName(option)}
                    {option.honorific && (
                      <small>
                        (
                        <CustomFormattedMessage
                          id={`common.honorific.${kebabCase(option.honorific)}`}
                        />
                        )
                      </small>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Fragment>
      ))}
    </ContextModal>
  );
}
